import { useState, useEffect, useCallback } from "react";
import { G, ADMIN_API, adminInput } from "../../design/tokens";

export default function ChatBotModule({ authHeader, canWrite }) {
  const [config, setConfig]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [toast, setToast]         = useState(null); // { msg, ok }

  const flash = useCallback((msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res  = await fetch(`${ADMIN_API}/chatbot`, { headers: authHeader });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setConfig(data);
      setNameInput(data.name || "Isabel");
    } catch (e) {
      setConfig({ name: "Isabel", isOnline: true, tokenWarning: false });
      setNameInput("Isabel");
    }
    setLoading(false);
  }, [authHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const putConfig = async (patch) => {
    setSaving(true);
    try {
      const res  = await fetch(`${ADMIN_API}/chatbot`, {
        method: "PUT",
        headers: authHeader,
        body: JSON.stringify(patch),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        flash(data.error || `Server error ${res.status}`, false);
      } else {
        // Always re-read from server to confirm persisted state
        setConfig(data.config || config);
        if (data.config?.name) setNameInput(data.config.name);
        flash(data.message || "Saved successfully");
      }
    } catch (e) {
      flash(`Request failed: ${e.message}`, false);
    }
    setSaving(false);
  };

  if (loading) return (
    <div style={{ padding: "28px 32px", fontFamily: "'Space Grotesk', sans-serif", color: "rgba(255,255,255,0.25)", fontSize: 14 }}>
      Loading chatbot settings…
    </div>
  );

  const isLive = config.isOnline && !config.tokenWarning;

  return (
    <div style={{ padding: "28px 32px", maxWidth: 720 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: "#fff" }}>
            ChatBot Settings
          </div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.3)", marginTop: 4 }}>
            Manage {config.name}'s availability and configuration
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 20, border: `1px solid ${isLive ? "rgba(52,211,153,0.3)" : "rgba(248,113,113,0.3)"}`, background: isLive ? "rgba(52,211,153,0.08)" : "rgba(248,113,113,0.08)" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: isLive ? "#34D399" : "#f87171", boxShadow: `0 0 8px ${isLive ? "#34D399" : "#f87171"}` }} />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 600, color: isLive ? "#34D399" : "#f87171" }}>
            {isLive ? "Online" : "Offline"}
          </span>
        </div>
      </div>

      {/* Token Warning Banner */}
      {config.tokenWarning && (
        <div style={{ background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.3)", borderRadius: 14, padding: "18px 22px", marginBottom: 20, display: "flex", alignItems: "flex-start", gap: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: "rgba(220,38,38,0.15)", border: "1px solid rgba(220,38,38,0.3)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 700, color: "#f87171", marginBottom: 4 }}>
              API Token Limit Reached
            </div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "rgba(248,113,113,0.7)", lineHeight: 1.6, marginBottom: 12 }}>
              {config.name} automatically went offline because the AI API hit a rate or token limit.
              {config.tokenWarningAt && (
                <span style={{ display: "block", marginTop: 4, color: "rgba(255,255,255,0.3)", fontSize: 11 }}>
                  Detected: {new Date(config.tokenWarningAt).toLocaleString()}
                </span>
              )}
            </div>
            {canWrite && (
              <button
                onClick={() => putConfig({ tokenWarning: false, isOnline: true })}
                disabled={saving}
                style={{ padding: "7px 18px", borderRadius: 8, border: "1px solid rgba(248,113,113,0.3)", background: "rgba(220,38,38,0.15)", color: "#f87171", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12, fontWeight: 600, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : "Clear Warning & Bring Online"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Availability Card */}
      <div style={{ background: G.glass, border: `1px solid ${G.glassBrd}`, borderRadius: 16, padding: "24px 26px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 5 }}>
              Chatbot Availability
            </div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "rgba(255,255,255,0.35)", lineHeight: 1.6, maxWidth: 380 }}>
              {config.isOnline
                ? `${config.name} is online and accepting inquiries from website visitors.`
                : `${config.name} is offline. Visitors will see an offline message with contact details.`}
            </div>
            {config.updatedAt && (
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.2)", marginTop: 8 }}>
                Last changed: {new Date(config.updatedAt).toLocaleString()} · {config.updatedBy || "admin"}
              </div>
            )}
          </div>

          {/* Toggle — proper button element */}
          <button
            type="button"
            onClick={() => !saving && canWrite && putConfig({ isOnline: !config.isOnline })}
            disabled={saving || !canWrite}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              background: "none", border: "none", padding: "4px 8px",
              cursor: saving ? "wait" : canWrite ? "pointer" : "default",
              opacity: saving ? 0.6 : 1, flexShrink: 0,
            }}
          >
            {/* Track */}
            <div style={{ width: 56, height: 28, borderRadius: 14, background: config.isOnline ? G.btnGrad : "rgba(255,255,255,0.1)", position: "relative", transition: "background 0.25s", boxShadow: config.isOnline ? "0 2px 10px rgba(37,99,235,0.4)" : "none" }}>
              {/* Thumb */}
              <div style={{ position: "absolute", top: 4, left: config.isOnline ? 32 : 4, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.25s", boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }} />
            </div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: 1, color: config.isOnline ? "#34D399" : "rgba(255,255,255,0.3)" }}>
              {saving ? "…" : config.isOnline ? "ON" : "OFF"}
            </span>
          </button>
        </div>
      </div>

      {/* Bot Name Card */}
      <div style={{ background: G.glass, border: `1px solid ${G.glassBrd}`, borderRadius: 16, padding: "24px 26px", marginBottom: 16 }}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>
          Bot Name
        </div>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "rgba(255,255,255,0.35)", marginBottom: 16 }}>
          The name shown in the chat widget header and offline message.
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && nameInput.trim() && canWrite && !saving) putConfig({ name: nameInput.trim() }); }}
            placeholder="Isabel"
            disabled={!canWrite || saving}
            style={adminInput({ flex: 1, marginBottom: 0, opacity: canWrite ? 1 : 0.5 })}
          />
          {canWrite && (
            <button
              type="button"
              onClick={() => nameInput.trim() && !saving && putConfig({ name: nameInput.trim() })}
              disabled={saving || !nameInput.trim()}
              style={{ padding: "10px 22px", borderRadius: 10, border: "none", background: G.btnGrad, color: "#fff", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, fontWeight: 600, cursor: saving || !nameInput.trim() ? "default" : "pointer", boxShadow: G.btnShadow, opacity: saving || !nameInput.trim() ? 0.5 : 1, flexShrink: 0, whiteSpace: "nowrap" }}>
              {saving ? "Saving…" : "Save Name"}
            </button>
          )}
        </div>
      </div>

      {/* Offline Preview */}
      <div style={{ background: G.glass, border: `1px solid ${G.glassBrd}`, borderRadius: 16, padding: "24px 26px" }}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 14, fontWeight: 600, color: "#fff", marginBottom: 6 }}>
          Offline Message Preview
        </div>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "rgba(255,255,255,0.35)", marginBottom: 16 }}>
          This is what visitors see when the chatbot is offline.
        </div>
        <div style={{ background: "rgba(4,10,24,0.5)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "28px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(248,113,113,0.1)", border: "2px solid rgba(248,113,113,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/>
            </svg>
          </div>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
              {nameInput || config.name} is currently offline
            </div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "rgba(255,255,255,0.4)", lineHeight: 1.7 }}>
              You may reach us directly:
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
            {[
              { icon: "✉", label: "inquiry@neswcorp.com" },
              { icon: "📞", label: "0998-859-0597" },
            ].map((c, i) => (
              <div key={i} style={{ padding: "10px 16px", borderRadius: 10, background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.15)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 12.5, color: "#93C5FD", display: "flex", alignItems: "center", gap: 8 }}>
                <span>{c.icon}</span>{c.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)", background: toast.ok ? "rgba(5,40,20,0.96)" : "rgba(50,5,5,0.96)", border: `1.5px solid ${toast.ok ? "rgba(52,211,153,0.5)" : "rgba(248,113,113,0.5)"}`, borderRadius: 14, padding: "14px 28px", fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: toast.ok ? "#34D399" : "#f87171", fontWeight: 600, zIndex: 9999, backdropFilter: "blur(16px)", boxShadow: `0 8px 40px rgba(0,0,0,0.6)`, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 16 }}>{toast.ok ? "✓" : "✕"}</span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
